<template>
  <div class="acts-page">
    <div class="sidebar-wrap">
      <aside class="categories">
        <button
          v-for="item in categories"
          :key="item.code"
          type="button"
          :class="{ active: category === item.code }"
          @click="category = item.code"
        >
          <EncryptedImg
            v-if="item.iconUrl"
            class="icon-img"
            :url="item.iconUrl"
            :key-hex="item.iconKey"
            eager
            fit="contain"
          />
          <div v-else class="icon-placeholder"></div>
          <span>{{ item.label }}</span>
        </button>
      </aside>
    </div>
    <section class="activity-list">
      <PromoSkeleton v-if="loading && !items.length" variant="card" :count="4" />
      <div v-else-if="!items.length" class="acts-hint">{{ t('promo.acts.empty') }}</div>
      <PromoActivityCard
        v-for="(item, idx) in items"
        :key="item.activityId || item.activityCode"
        :style="{ '--i': Math.min(idx, 8) }"
        :activity="item"
        @open="openDetail"
        @action="onAction"
      />
    </section>

    <PromoActivityDetailDrawer
      v-model="detailOpen"
      :activity="selected"
      :loading="detailLoading"
      @close="detailOpen = false"
      @action="onAction"
    />

    <PromoBirthdaySheet
      v-model="birthdayOpen"
      :submitting="birthdaySubmitting"
      @submit="submitBirthday"
    />
  </div>
</template>

<script setup lang="ts">
import EncryptedImg from '~/components/EncryptedImg.vue'
import type { PromoActivity } from './types'
import PromoSkeleton from '../components/PromoSkeleton.vue'
import PromoActivityCard from './components/PromoActivityCard.vue'
import PromoActivityDetailDrawer from './components/PromoActivityDetailDrawer.vue'
import PromoBirthdaySheet from './components/PromoBirthdaySheet.vue'
import { usePromoActivities } from './composables/usePromoActivities'

const { t } = useI18n()

const {
  categories,
  category,
  items,
  loading,
  runAction,
  detailOpen,
  detailLoading,
  selected,
  openDetail,
  birthdayOpen,
  birthdaySubmitting,
  submitBirthday,
} = usePromoActivities()

const onAction = (activity: PromoActivity) => {
  void runAction(activity)
}
</script>

<style scoped lang="scss">
.acts-page {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px 0;
}

.sidebar-wrap {
  flex: 0 0 66px;
}

.categories {
  position: fixed;
  left: 14px;
  z-index: 10;
  width: 66px;
  max-height: var(--lobside-maxh, 74vh);
  overflow-y: auto;
  scrollbar-width: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: var(--tabbar-h);
}

.categories button {
  padding: 11px 2px;
  border-radius: var(--r-md);
  background: var(--surface);
  border: 1px solid var(--hairline);
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
}

.categories button.active {
  background: var(--surface2);
  color: var(--primary);
  border-color: var(--primary);
}

.categories .icon {
  display: block;
  font-size: 30px;
  margin-bottom: 5px;
  line-height: 1;
}

.categories .icon-img {
  display: block;
  width: 30px;
  height: 30px;
  margin: 0 auto 5px;
}

.categories .icon-placeholder {
  display: block;
  width: 30px;
  height: 30px;
  margin: 0 auto 5px;
}

.acts-hint {
  padding: 40px 0;
  text-align: center;
  color: var(--muted);
  font-size: 13px;
}

.activity-list {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 10px;
}
</style>
